/**
 * Shared Supabase client singleton.
 *
 * Used by:
 * - useSupabaseRealtime (CDC subscriptions, presence, typing)
 * - useConversationRealtime (message CDC)
 * - useFileUpload (Storage uploads)
 *
 * The client is constructed from values in /config.json (loaded once
 * at boot via `loadRuntimeConfig()` in main.tsx). One Docker image
 * works on staging and production; the orchestrator supplies the
 * SUPABASE_URL + SUPABASE_ANON_KEY env vars per environment.
 */

import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { getRuntimeConfig, loadRuntimeConfig } from './runtimeConfig';

let _client: SupabaseClient | null = null;

/**
 * Async initialization. Safe to call multiple times — idempotent.
 * Must complete before any consumer calls `getSupabase()`.
 */
export async function initSupabase(): Promise<SupabaseClient> {
  const cfg = await loadRuntimeConfig();
  if (!_client) {
    _client = createClient(cfg.supabaseUrl, cfg.supabaseAnonKey, {
      auth: { persistSession: true, autoRefreshToken: true },
    });
  }
  return _client;
}

/**
 * Synchronous accessor for the Supabase client. Throws if neither
 * `initSupabase()` nor `loadRuntimeConfig()` has resolved yet.
 *
 * Hooks should call this inside `useEffect` (after first render),
 * never at module-import time.
 */
export function getSupabase(): SupabaseClient {
  if (_client) return _client;
  // Falls through to throwing getRuntimeConfig() if config never loaded.
  const cfg = getRuntimeConfig();
  _client = createClient(cfg.supabaseUrl, cfg.supabaseAnonKey, {
    auth: { persistSession: true, autoRefreshToken: true },
  });
  return _client;
}

/**
 * Test-only escape hatch.
 */
export function __resetSupabaseForTests(): void {
  _client = null;
}
